import { AuthService } from '@core/services/auth'
import { AxiosError } from 'axios'
import { clearMdrLocalStorage } from '@core/modules/mdr/utils'

export const STATUS_CODES_REDIRECT_TO_LOGIN = [401, 403, 417]

type TRetryConfig = {
    retry: {
        maxRetries?: number
        retryDelay?: number
    }
}

type TPollConfig<T = unknown> = {
    poll: {
        maxRetries?: number
        delay?: number
        delayMultiplier?: number
        shouldStop: (
            response: T | null,
            retriesCompleted: number
        ) => Promise<boolean | null> | boolean | null
        resultsCallback?: (response: T | null) => Promise<void> | void
    }
}

async function poll<T, U = Error & AxiosError>(
    fn: () => Promise<T>,
    config: TPollConfig<T>
): Promise<[U | null, T | undefined]> {
    const {
        maxRetries = 10000,
        delay = 1000,
        delayMultiplier = 1,
        shouldStop,
        resultsCallback,
    } = config.poll

    for (let retries = 0; retries <= maxRetries; retries++) {
        try {
            const response: Awaited<any> = await fn()

            if (await shouldStop(response, retries)) {
                resultsCallback?.(response?.data?.data)
                return [null, response]
            }

            const currentDelay = delay * Math.pow(delayMultiplier, retries)

            resultsCallback?.(response?.data?.data)

            await new Promise((resolve) => setTimeout(resolve, currentDelay))
        } catch (error) {
            return [error as U, undefined]
        }
    }

    return [undefined as unknown as U, undefined]
}

async function retry<T, U = Error & AxiosError>(
    fn: () => Promise<T>,
    config: TRetryConfig
): Promise<[U | null, T | undefined]> {
    const maxRetries = config?.retry?.maxRetries ?? 0
    const retryDelay = config?.retry?.retryDelay ?? 1000

    for (let retries = 0; retries <= maxRetries; retries++) {
        try {
            const result = await fn()
            return [null, result]
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                if (
                    STATUS_CODES_REDIRECT_TO_LOGIN.includes(
                        error.response?.status as number
                    )
                ) {
                    clearMdrLocalStorage()
                    await AuthService.refresh()
                }
            }

            if (retries >= maxRetries) {
                return [error as U, undefined]
            } else {
                await new Promise((resolve) => setTimeout(resolve, retryDelay))
            }
        }
    }

    return [undefined as unknown as U, undefined]
}

export async function request<T, U = Error & AxiosError>(
    fn: () => Promise<T>,
    config = {} as TRetryConfig | TPollConfig<T>
): Promise<[U | null, T | undefined]> {
    if ('poll' in config) {
        // poll
        return poll(fn, config)
    }
    // retry or once if no config
    return retry(fn, config)
}
